<template>
  <FormCard
    data-testid="self-service-form-linkedin-group-job-details"
    data-group-key="job-details"
    :title="linkedinJobDetails"
    :class="{ 'selected-card': openCard === `${linkedinJobDetails}` }"
    :is-open="openCard === `${linkedinJobDetails}`"
    @toggle="emit('toggle', `${linkedinJobDetails}`)"
    :completed="completedCards.has(`${linkedinJobDetails}`)"
    @enter-pressed="handleSaveAndContinue">
    <template #description>
      <div class="job-details-summary">
        {{ workplaceLabel }}
        <span class="separator">/</span>
        {{ employmentLabel }}
        <span class="separator">/</span>
        {{ experienceLabel }}
      </div>
    </template>
    <template #form-content>
      <div class="form-section" data-testid="self-service-field-workplace_type" data-field-key="workplace_type">
        <LeadSelect
          v-model="workplaceType"
          :options="workplaceTypes"
          :label="workplaceTranslation"
          @update:model-value="(type) => (obj.workplace_type = String(type))" />
      </div>
      <div class="form-section" data-testid="self-service-field-employment_status" data-field-key="employment_status">
        <LeadSelect
          v-model="employmentStatus"
          :options="employmentStatuses"
          :label="employmentTranslation"
          @update:model-value="(status) => (obj.employment_status = String(status))" />
      </div>
      <div class="form-section" data-testid="self-service-field-experience_level" data-field-key="experience_level">
        <LeadSelect
          v-model="experienceLevel"
          :options="experienceLevels"
          :label="experienceTranslation"
          @update:model-value="(level) => (obj.experience_level = String(level))" />
      </div>
      <div
        class="form-section"
        v-if="fieldGroups?.[1]?.properties?.[3]"
        data-testid="self-service-field-poster_email"
        data-field-key="poster_email">
        <LeadInput
          v-model="posterEmail"
          :label="posterEmailLabel"
          type="email"
          :placeholder="$t('campaigns.please_write')" />
      </div>
      <div class="float-right" data-testid="self-service-save-action-job-details" data-i18n-key="btn.save_and_continue">
        <LeadButton variant="secondary" :loading="loadingSave" @click="handleSaveAndContinue">
          {{ $t('btn.save_and_continue') }}
          <template #icon>
            <IconArrowBigRightFilled size="16" stroke-width="1.5" />
          </template>
        </LeadButton>
      </div>
    </template>
  </FormCard>
</template>

<script setup lang="ts">
import FormCard from '@/components/orders/form_cards/FormCard.vue';
import LeadButton from '../generic/LeadButton.vue';
import { IconArrowBigRightFilled } from '@tabler/icons-vue';
import { ref, computed, watch, type Ref } from 'vue';
import { updateCampaignObject } from '@/api/campaign';
import { useToast } from 'vue-toastification';
import type { FieldGroup } from '@/types/types';
import LeadSelect from './LeadSelect.vue';
import { useProviderStore } from '@/stores/providers';
import { storeToRefs } from 'pinia';
import type { LinkedInForm } from '@/components/orders/details/provider_forms/LinkedInForm.vue';
import translator from '@/locale/translator';
import LeadInput from './LeadInput.vue';

const props = defineProps<{
  providerId: string | null | undefined;
  openCard: string | null;
  completedCards: Set<string>;
  object: any;
  fieldGroups: FieldGroup[];
}>();

const emit = defineEmits<{
  (e: 'toggle', cardKey: string): void;
  (e: 'completed', currentCardKey: string, nextCardKey: string | null): void;
  (e: 'update:object', obj: any): void;
}>();

const toast = useToast();
const providerStore = useProviderStore();
const loadingSave = ref(false);
const { obj } = storeToRefs(providerStore) as unknown as { obj: Ref<LinkedInForm> };

const workplaceType = ref<string>();
const employmentStatus = ref<string>();
const experienceLevel = ref<string>();
const posterEmail = ref<string>();

const workplaceTranslation = computed(() => {
  if (!props.object?.groups?.[1]?.properties?.[1]) return 'Workplace Type';
  return props.object.groups[1].properties[1].label || 'Workplace Type';
});
const employmentTranslation = computed(() => {
  if (!props.object?.groups?.[1]?.properties?.[0]) return 'Employment Status';
  return props.object.groups[1].properties[0].label || 'Employment Status';
});
const experienceTranslation = computed(() => {
  if (!props.object?.groups?.[1]?.properties?.[2]) return 'Experience Level';
  return props.object.groups[1].properties[2].label || 'Experience Level';
});

const workplaceTypes = ref<{ label: string; value: string }[]>([]);
const employmentStatuses = ref<{ label: string; value: string }[]>([]);
const experienceLevels = ref<{ label: string; value: string }[]>([]);

const workplaceLabel = computed(() => {
  if (!workplaceType.value) return workplaceTranslation.value;
  return workplaceTypes.value.find((wt) => wt.value === workplaceType.value)?.label || '';
});

const employmentLabel = computed(() => {
  if (!employmentStatus.value) return employmentTranslation.value;
  return employmentStatuses.value.find((es) => es.value === employmentStatus.value)?.label || '';
});

const experienceLabel = computed(() => {
  if (!experienceLevel.value) return experienceTranslation.value;
  return experienceLevels.value.find((el) => el.value === experienceLevel.value)?.label || '';
});

const posterEmailLabel = computed(() => {
  if (!props.object?.groups?.[1]?.properties?.[3]) return 'Poster Email';
  return props.object.groups[1].properties[3].label || 'Poster Email';
});

const linkedinJobDetails = computed(() => {
  if (!props.object?.groups?.[1]) return 'LinkedIn Job Details';
  return props.object.groups[1].name || 'LinkedIn Job Details';
});

const handleSaveAndContinue = async () => {
  if (workplaceType.value && employmentStatus.value && experienceLevel.value) {
    try {
      loadingSave.value = true;
      const payload: {
        workplace_type: string;
        employment_status: string;
        experience_level: string;
        poster_email?: string;
      } = {
        workplace_type: workplaceType.value,
        employment_status: employmentStatus.value,
        experience_level: experienceLevel.value,
      };

      if (props.fieldGroups?.[1]?.properties?.[3]) {
        payload.poster_email = posterEmail.value;
      }

      const res = await updateCampaignObject(props.providerId ?? '', payload);

      setTimeout(() => {
        loadingSave.value = false;
        providerStore.addCompletedCard(`${linkedinJobDetails.value}`, props.providerId ?? '');
        emit('completed', `${linkedinJobDetails.value}`, `${props.object.groups[2]?.name || null}`);
      }, 500);
      emit('update:object', res.data);
    } catch (error) {
      console.error('Error updating campaign object:', error);
      toast.error(translator.get('campaign.error_saving'));
      loadingSave.value = false;
    }
  } else {
    toast.error(translator.get('campaigns.please_fill_in'));
  }
};

watch(
  () => props.object,
  (newObj) => {
    if (newObj?.groups?.[1]) {
      const secondGroup = newObj.groups[1];
      employmentStatus.value = secondGroup?.properties?.[0]?.value || null;
      workplaceType.value = secondGroup?.properties?.[1]?.value || null;
      experienceLevel.value = secondGroup?.properties?.[2]?.value || null;
      posterEmail.value = secondGroup?.properties?.[3]?.value || null;
    }
  },
  { immediate: true }
);

watch(
  () => props.fieldGroups,
  (fieldG) => {
    if (fieldG?.length > 1 && fieldG[1]?.properties) {
      const secondGroup = fieldG[1].properties;
      employmentStatuses.value = secondGroup[0]?.options || [];
      workplaceTypes.value = secondGroup[1]?.options || [];
      experienceLevels.value = secondGroup[2]?.options || [];
    }
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.float-right {
  float: right;
}

.form-section {
  margin-bottom: 30px;
}

.job-details-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0;
  white-space: nowrap;

  .separator {
    color: #afafaf;
    margin: 0 6px;
  }
}
</style>
